import React, { useState, useMemo } from 'react';
import { Col, Divider, Row, Skeleton } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { useApplicationContext } from '@/context/v1/Application/context';

import { Title } from '@/components/Typography';
import { TaskResult } from '@/types/Bera';
import { useGetReportResult } from '@/hooks/useGetReportResult';
import { Steps } from '../../BeraFormSteps/BeraStep/BeraResult/Steps';
import { KimManualHandlingResult } from '../../BeraFormSteps/BeraStep/BeraResult/KimManualHandling';
import { NioshResult } from '../../BeraFormSteps/BeraStep/BeraResult/Niosh';
import { StrainIndexResult } from '../../BeraFormSteps/BeraStep/BeraResult/StrainIndex';

interface TasksProps {
	bera_job_summary_id: string;
	isLoading: boolean;
	tasks: TaskResult[];
}

export function Tasks({ isLoading, tasks = [], bera_job_summary_id }: Readonly<TasksProps>): JSX.Element {
	const [openBeraReportId, setOpenBeraReportId] = useState<string | null>(null);

	const { organization, company } = useApplicationContext();

	const { mutateAsync: getReportResult, data: reportResult, isLoading: isGettingReportResult } = useGetReportResult();

	const lastOpenedBeraReportId = useMemo(() => openBeraReportId, [openBeraReportId]);

	function toggleTask(task: TaskResult) {
		if (lastOpenedBeraReportId === task.bera_report_id) {
			setOpenBeraReportId(null);
			return;
		}

		setOpenBeraReportId(task.bera_report_id);

		getReportResult({
			organization_id: organization?.id,
			company_id: company?.id,
			file_id: task.file_id,
			bera_job_summary_id: bera_job_summary_id
		});
	}

	if (isLoading) {
		return <Skeleton active />;
	}

	function renderReportResult(): JSX.Element {
		if (isGettingReportResult) {
			return <Skeleton active />;
		}

		const lastStep = reportResult?.bera_steps_result[reportResult?.bera_steps_result.length - 1];
		const hasTools =
			!!reportResult?.tools.niosh || !!reportResult?.tools.kim_mho || !!reportResult?.tools.strain_index;
		const nioshHasDivider = reportResult?.tools.niosh && reportResult.tools.kim_mho;
		const kimMhoHasDivider = reportResult?.tools.kim_mho && reportResult.tools.strain_index;

		return (
			<Col span={24}>
				{reportResult?.bera_steps_result.map((step) => {
					const hasDivider = step.id !== lastStep?.id && hasTools;

					return (
						<React.Fragment key={step.id}>
							<Steps stepName={step.description} stepKeys={step.step_key} />
							{hasDivider && <Divider type="horizontal" />}
						</React.Fragment>
					);
				})}

				{reportResult?.tools.niosh && (
					<>
						<NioshResult niosh={reportResult.tools.niosh} />
						{nioshHasDivider && <Divider type="horizontal" />}
					</>
				)}

				{reportResult?.tools.kim_mho && (
					<>
						<KimManualHandlingResult kim_mho={reportResult.tools.kim_mho} />
						{kimMhoHasDivider && <Divider type="horizontal" />}
					</>
				)}

				{reportResult?.tools.strain_index && (
					<StrainIndexResult strain_index={reportResult.tools.strain_index} />
				)}
			</Col>
		);
	}

	return (
		<>
			{tasks.map((task) => {
				const isOpen = openBeraReportId === task.bera_report_id;

				return (
					<React.Fragment key={task.id}>
						<Row
							justify="center"
							onClick={() => toggleTask(task)}
							style={{ cursor: 'pointer', marginTop: '2rem' }}
						>
							<Col span={22}>
								<Row>
									<Title level={5}>
										{task.name} : {task.file_name}
									</Title>
								</Row>
							</Col>

							<Col span={2} style={{ display: 'flex', justifyContent: 'end' }}>
								{isOpen ? <UpOutlined /> : <DownOutlined />}
							</Col>
						</Row>

						<Divider type="horizontal" />

						{isOpen && renderReportResult()}
					</React.Fragment>
				);
			})}
		</>
	);
}
